import { haversineMeters } from '@traintopia/domain'
import type { StopEntry } from '../store/stops'
import type { VehiclePosition, VehicleType } from '../store/transit'

function distanceBetween(lat1: number, lon1: number, lat2: number, lon2: number): number {
  return haversineMeters(
    { latitude: lat1, longitude: lon1 },
    { latitude: lat2, longitude: lon2 },
  )
}

type StopKind = 'subway' | 'bus'

interface StationOptions {
  kind?: StopKind
  maxDistanceMeters?: number
  limit?: number
}

export interface NearestStation {
  station: StopEntry
  distanceMeters: number
}

interface VehicleOptions {
  maxDistanceMeters?: number
  type?: VehicleType | 'all'
  limit?: number
}

export interface NearestVehicle {
  vehicle: VehiclePosition
  distanceMeters: number
}

/**
 * Builds a nearest-stations scanner. A FACTORY rather than a bare function because the
 * bus-stop scan is throttled against a cache of the last result, and that cache must be
 * per-caller: a module-level one would be shared, and two callers passing different
 * `kind`/`limit` would invalidate each other on alternate calls.
 *
 * Call once per component, then re-invoke inside a `$derived` for memoisation.
 *
 * `kind` selects the source: 'subway' (~500 stations) or 'bus' (~15k stops). The subway set
 * is small enough to rescan every GPS tick; the bus set only re-runs once the user has moved
 * >25 m, or the source list changed, returning the previous result by reference between.
 */
export function makeNearestStations() {
  let last: { lat: number; lon: number; list: StopEntry[]; result: NearestStation[] } | null = null

  return function nearestStations(
    stops: StopEntry[],
    userLocation: { latitude: number; longitude: number } | null,
    options: StationOptions = {},
  ): NearestStation[] {
    const { kind = 'subway', maxDistanceMeters = 1500, limit = 5 } = options
    if (!userLocation || stops.length === 0) return []

    // Skip the bus rescan when nothing material changed: same source list, and the user has
    // moved ≤25 m since the last computed position. Subway (small) always
    // recomputes so distances stay live.
    if (
      kind === 'bus' &&
      last &&
      last.list === stops &&
      distanceBetween(last.lat, last.lon, userLocation.latitude, userLocation.longitude) <= 25
    ) {
      return last.result
    }

    const result = stops
      .map((station) => ({
        station,
        distanceMeters: distanceBetween(
          userLocation.latitude,
          userLocation.longitude,
          station.lat,
          station.lon,
        ),
      }))
      .filter((entry) => entry.distanceMeters <= maxDistanceMeters)
      .sort((a, b) => a.distanceMeters - b.distanceMeters)
      .slice(0, limit)

    last = { lat: userLocation.latitude, lon: userLocation.longitude, list: stops, result }
    return result
  }
}

/** Vehicles nearest the user, sorted nearest-first. Pure — memoise with `$derived`. */
export function nearestVehicles(
  vehicles: Record<string, VehiclePosition>,
  userLocation: { latitude: number; longitude: number } | null,
  options: VehicleOptions = {},
): NearestVehicle[] {
  const { maxDistanceMeters = 500, type = 'all', limit = 5 } = options
  if (!userLocation) return []

  return Object.values(vehicles)
    .filter((v) => type === 'all' || v.type === type)
    .map((v) => ({
      vehicle: v,
      distanceMeters: distanceBetween(
        userLocation.latitude,
        userLocation.longitude,
        v.latitude,
        v.longitude,
      ),
    }))
    .filter((entry) => entry.distanceMeters <= maxDistanceMeters)
    .sort((a, b) => a.distanceMeters - b.distanceMeters)
    .slice(0, limit)
}
